import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, LogOut, ShieldAlert, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

/**
 * Waiting Screen shown to users whose company is `pending` or `rejected`.
 * Subscribes to /api/auth/status-stream (SSE). When the master admin approves
 * the company, the stream pushes a `company_status=active` event and this
 * screen automatically navigates the user to /app — no refresh needed.
 */
export default function Pending() {
  const { user, company, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const esRef = useRef(null);
  const status = company?.status || "pending";
  const rejected = status === "rejected";

  useEffect(() => {
    if (!user || user === false) return;
    // If company is already active (edge case where the tab lingered), redirect.
    if (status === "active") { navigate("/app", { replace: true }); return; }

    const token = localStorage.getItem("ss_token");
    if (!token) return;
    const backend = process.env.REACT_APP_BACKEND_URL;
    const url = `${backend}/api/auth/status-stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url, { withCredentials: false });
    esRef.current = es;

    const onStatus = async (evt) => {
      try {
        const payload = JSON.parse(evt.data);
        if (payload.type === "company_status") {
          if (payload.status === "active") {
            toast.success("Your company was approved — welcome aboard.");
            await refresh();
            navigate("/app", { replace: true });
          } else if (payload.status === "rejected") {
            await refresh();
          }
        }
      } catch (_) { /* ignore */ }
    };
    es.addEventListener("status", onStatus);
    es.onerror = () => {
      // Silent; EventSource auto-reconnects. As a fallback, poll every 15s.
    };

    const poll = setInterval(async () => {
      try { await refresh(); } catch { /* ignore */ }
    }, 15000);

    return () => {
      es.close();
      clearInterval(poll);
    };
  }, [user, status, refresh, navigate]);

  const Icon = rejected ? ShieldAlert : Clock;
  const color = rejected ? "text-destructive" : "text-primary";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="pending-screen">
      <div className="max-w-lg w-full">
        <div className="overline mb-2">SiteSync</div>
        <div className={`flex items-center gap-3 mb-6 ${color}`}>
          <Icon className="h-10 w-10" strokeWidth={1.6} />
          <div>
            <div className="text-2xl font-semibold tracking-tight" data-testid="pending-heading">
              {rejected ? "Registration declined" : "Awaiting activation"}
            </div>
            <div className="text-xs text-muted-foreground font-mono uppercase tracking-widest mt-0.5">
              status · {status}
            </div>
          </div>
        </div>

        <p className="text-base leading-relaxed text-foreground mb-2" data-testid="pending-message">
          {rejected
            ? "Your registration was declined by SiteSync. If you believe this is a mistake, contact support so we can review your account."
            : "Your registration is under review. Our team will activate your account shortly."}
        </p>

        {!rejected && (
          <p className="text-sm text-muted-foreground mb-8 flex items-center gap-2" data-testid="pending-realtime-hint">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            This page will update automatically the moment your account is approved — no refresh needed.
          </p>
        )}

        <div className="border border-border rounded-sm p-5 bg-muted/30 mb-8">
          <div className="grid grid-cols-1 gap-2 text-sm">
            <MetaRow label="Signed in as" value={user?.email} />
            <MetaRow label="Company" value={company?.name} />
            <MetaRow label="Submitted" value={company?.created_at ? new Date(company.created_at).toLocaleString() : "—"} />
          </div>
        </div>

        {!rejected && (
          <div className="flex items-start gap-2 text-xs text-muted-foreground border border-dashed border-border rounded-sm p-3 mb-6">
            <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span>You will keep the same email and password. As soon as we approve you, the app opens automatically in this tab.</span>
          </div>
        )}

        <Button variant="outline" className="rounded-sm border-border" onClick={logout} data-testid="pending-logout-btn">
          <LogOut className="h-4 w-4 mr-1.5" /> Sign out
        </Button>
      </div>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="overline">{label}</span>
      <span className="font-mono text-sm text-foreground truncate">{value || "—"}</span>
    </div>
  );
}
